import { motion } from "motion/react";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import WhatsAppFloating from "../../../components/feature/WhatsAppFloating";
import CookieBanner from "../../../components/feature/CookieBanner";
import QuoteFormSection from "../../home/components/QuoteFormSection";
import SEO from "@/components/feature/SEO";

const ventajas = [
  {
    emoji: "🎮",
    title: "Comodidad",
    desc: "Abre o cierra tu toldo desde el sofá, la cama o la piscina. Con un solo botón del mando o desde la app del móvil.",
  },
  {
    emoji: "💨",
    title: "Sensor de viento",
    desc: "Si sopla fuerte, el sensor recoge automáticamente el toldo para proteger la estructura y la lona. Sin preocuparte.",
  },
  {
    emoji: "☀️",
    title: "Sensor de sol",
    desc: "El toldo se extiende solo cuando el sol calienta y se recoge cuando refresca. Automático y eficiente.",
  },
  {
    emoji: "🏠",
    title: "Domótica",
    desc: "Compatible con Alexa, Google Home y Somfy TaHoma. Controla con la voz o programa horarios desde el móvil.",
  },
];

const componentes = [
  {
    icon: "ri-flashlight-line",
    title: "Motores Somfy",
    desc: "Líder mundial en automatización de exterior. Garantía oficial de 5 años. Potencia silenciosa y duradera.",
  },
  {
    icon: "ri-sun-line",
    title: "Lonas Tempotest, Sauleda, Recasens",
    desc: "Lonas técnicas anti-UV y anti-decoloración. Resisten el sol intenso de Málaga sin perder color ni elasticidad.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Estructura aluminio lacado",
    desc: "Perfiles de aluminio de alta resistencia con tratamiento anti-salitre. Ideal para la Costa del Sol y el ambiente marino.",
  },
];

const steps = [
  { num: "1", title: "Visita técnica + medición gratis", desc: "Acudimos a tu hogar, medimos tu terraza o balcón y evaluamos la fachada. Sin coste ni compromiso." },
  { num: "2", title: "Diseño 3D + presupuesto detallado", desc: "Te mostramos cómo quedará tu toldo motorizado con un diseño en 3D. Presupuesto claro y por escrito." },
  { num: "3", title: "Pedido y fabricación a medida", desc: "Fabricamos tu toldo exacto a las medidas de tu espacio. Plazo: 15-20 días para lonas estándar." },
  { num: "4", title: "Instalación profesional", desc: "Nuestro equipo instala todo en una sola jornada. Limpio, rápido y sin obras innecesarias." },
  { num: "5", title: "Entrega + tutorial uso", desc: "Te explicamos cómo usar el mando, programar horarios y conectar con apps. Y te dejamos garantía por escrito." },
];

const precios = [
  { tipo: "Toldo motorizado balcón", precio: "600 - 900 €", emoji: "🏠" },
  { tipo: "Toldo motorizado terraza pequeña (3x2,5m)", precio: "1.000 - 1.400 €", emoji: "🌅" },
  { tipo: "Toldo motorizado terraza grande (5x3m)", precio: "1.500 - 2.200 €", emoji: "🏖️" },
  { tipo: "Pérgola motorizada", precio: "2.500 - 5.000 €", emoji: "🏗️" },
];

const somfy = [
  { text: "Líder mundial en automatización de exteriores desde hace más de 50 años." },
  { text: "Sede en Cluses, Francia: calidad europea certificada." },
  { text: "Garantía oficial de 5 años en todos sus motores." },
  { text: "Apps móviles intuitivas: Somfy Protect, TaHoma." },
  { text: "Compatible con Alexa, Google Assistant y Apple HomeKit." },
  { text: "Servicio técnico oficial en toda España." },
];

const faqs = [
  {
    q: "¿Cuánto dura un motor Somfy?",
    a: "Un motor Somfy de calidad dura entre 15 y 20 años con uso normal. Ofrecemos garantía oficial de 5 años. Si tiene algún problema en ese periodo, Somfy lo sustituye sin coste.",
  },
  {
    q: "¿Funciona el toldo motorizado sin luz?",
    a: "Necesita corriente eléctrica para funcionar. Si hay un corte de luz, puedes bajarlo manualmente con la manivela de emergencia. También existen motores con batería solar opcionales.",
  },
  {
    q: "¿Necesito permiso de la comunidad?",
    a: "Para instalar un toldo en fachada exterior de un edificio de comunidad, normalmente es necesario informar o pedir autorización. Te asesoramos en todo el proceso y te damos el informe técnico que necesites presentar.",
  },
  {
    q: "¿Cómo se programa el sensor de viento?",
    a: "Durante la instalación configuramos el sensor de viento según la orientación de tu terraza y la zona donde vives. Puedes ajustar la sensibilidad tú mismo desde el mando o la app TaHoma.",
  },
  {
    q: "¿Puedo controlarlo desde el móvil?",
    a: "Sí, con la app Somfy TaHoma o Connexoon. Desde cualquier lugar del mundo abres, cierras o programas horarios para tu toldo. También funciona con comandos de voz si tienes Alexa o Google Home.",
  },
];

export default function ToldosMotorizados() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Toldos Motorizados Málaga | Motor Somfy Garantía 5 Años"
        description="Instalación de toldos motorizados en Málaga con motores Somfy. Sensor viento y sol. Garantía 5 años. ☎ 951 12 07 70"
        canonical="/toldos/toldos-motorizados/"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Toldos", path: "/toldos" }, { name: "Toldos motorizados" }]}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="bg-white pt-24 md:pt-[72px]">
          <div className="max-w-container mx-auto px-4 md:px-6 py-12 md:py-24">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="w-full lg:w-[60%] flex flex-col gap-6">
                <div className="inline-flex items-center gap-2 bg-accent text-text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill w-fit">
                  <span>⚡</span> Garantía motor 5 años
                </div>
                <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-[56px] leading-[1.1] text-text-primary">
                  Toldos Motorizados en Málaga: Instalación con Garantía
                </h1>
                <p className="font-body text-lg md:text-xl text-text-secondary leading-relaxed max-w-xl">
                  Comodidad máxima con un solo botón. Instalación profesional con motores Somfy. Garantía 5 años.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a
                    href="tel:+34951120770"
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg md:text-xl px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
                  >
                    <span>📞</span> 951 12 07 70
                  </a>
                  <a
                    href="#presupuesto"
                    className="inline-flex items-center gap-2 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
                  >
                    Pedir presupuesto
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-2">
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> +10 años · desde 2015</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Motores Somfy</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Garantía 5 años motor</span>
                </div>
              </div>
              <div className="w-full lg:w-[40%] relative">
                <div className="relative rounded-3xl overflow-hidden lg:-rotate-1 shadow-xl">
                  <div className="absolute inset-0 bg-blue-900/35 z-10 pointer-events-none"></div>
                  <img
                    src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80&auto=format&fit=crop"
                    alt="Toldos motorizados en Málaga - motor Somfy con garantía"
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VENTAJAS */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Ventajas de la motorización</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {ventajas.map((v, i) => (
                <motion.div
                  key={v.title}
                  className="bg-white rounded-[20px] p-8 border-t-[6px] border-primary shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-4xl mb-4">{v.emoji}</div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{v.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* COMPONENTES */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Componentes de calidad</h2>
              <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
                Solo instalamos materiales de primeras marcas europeas. Tu toldo motorizado debe durar años en el clima de Málaga.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {componentes.map((c, i) => (
                <motion.div
                  key={c.title}
                  className="bg-surface-alt rounded-[20px] p-8 shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-5">
                    <i className={`${c.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{c.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{c.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESO 5 STEPS */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Proceso de instalación</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {steps.map((s, i) => (
                <motion.div
                  key={s.num}
                  className="relative text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center font-heading font-extrabold text-xl mx-auto mb-4">
                    {s.num}
                  </div>
                  <h3 className="font-heading font-bold text-base text-text-primary mb-2">{s.title}</h3>
                  <p className="font-body text-xs text-text-secondary leading-relaxed">{s.desc}</p>
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-7 left-[calc(50%+1.75rem)] right-[-50%] h-0.5 bg-border"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PRECIOS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Precios orientativos</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary mt-4 max-w-2xl mx-auto">
                Precios con motor Somfy, lona de calidad, instalación completa y garantía de 5 años en motor incluida.
              </p>
            </div>
            <div className="max-w-2xl mx-auto bg-surface-alt rounded-3xl overflow-hidden">
              {precios.map((p, i) => (
                <div
                  key={p.tipo}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== precios.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="flex items-center gap-3 font-body text-base md:text-lg text-text-primary">
                    <span>{p.emoji}</span> {p.tipo}
                  </span>
                  <span className="font-heading font-bold text-lg md:text-xl text-primary">{p.precio}</span>
                </div>
              ))}
            </div>
            <p className="text-center font-body text-sm text-text-secondary mt-6">
              Precios orientativos según dimensiones, tipo de brazo y acabados. Presupuesto exacto sin compromiso tras visita técnica.
            </p>
          </div>
        </section>

        {/* ¿POR QUÉ SOMFY? */}
        <section className="bg-text-primary py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="w-full lg:w-1/2">
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">¿Por qué Somfy?</h2>
                <p className="font-body text-lg text-slate-300 mb-6">
                  Somfy es el líder mundial en automatización de exteriores. Sus motores son sinónimo de calidad, silencio y durabilidad.
                </p>
                <div className="flex flex-col gap-3">
                  {somfy.map((s, i) => (
                    <motion.div
                      key={i}
                      className="flex items-center gap-3 bg-slate-800 rounded-xl px-5 py-4 border border-slate-700"
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.08 }}
                    >
                      <span className="flex items-center justify-center w-8 h-8 bg-primary/20 text-primary rounded-full shrink-0 font-bold text-sm">✓</span>
                      <span className="font-body text-sm text-slate-300">{s.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-1/2 bg-slate-800 rounded-3xl p-8 md:p-10 border border-slate-700">
                <h3 className="font-heading font-bold text-2xl text-white mb-6">¿Por qué elegirnos a nosotros?</h3>
                <ul className="flex flex-col gap-4">
                  {[
                    "Somos instaladores certificados Somfy en Málaga",
                    "Garantía de 5 años directa con Somfy España",
                    "Servicio con nuestro equipo, sin intermediarios",
                    "Asesoramiento gratuito para elegir el motor adecuado",
                    "Instalación limpia en una sola jornada",
                    "Configuramos apps y domótica incluido",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-primary text-white rounded-full shrink-0 text-xs font-bold mt-0.5">✓</span>
                      <span className="font-body text-sm text-slate-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA PROMINENTE */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Pide presupuesto sin compromiso</h2>
            <p className="font-body text-lg text-text-secondary max-w-xl mx-auto mb-8">
              Te visitamos gratis, medimos tu espacio y te damos un presupuesto detallado en 24h. Sin compromiso.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+34951120770"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
              >
                <span>📞</span> 951 12 07 70
              </a>
              <a
                href="#presupuesto"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
              >
                Pedir presupuesto online
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto" itemScope itemType="https://schema.org/FAQPage">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Preguntas frecuentes sobre toldos motorizados</h2>
              <div className="flex flex-col gap-4">
                {faqs.map((f, i) => (
                  <motion.div
                    key={i}
                    className="bg-surface-alt rounded-2xl p-6"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <h3 className="font-heading font-bold text-lg text-text-primary mb-2" itemProp="name">{f.q}</h3>
                    <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                      <p className="font-body text-sm text-text-secondary leading-relaxed" itemProp="text">{f.a}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}